import { create } from "zustand";
import {
  deleteNotification as apiDelete,
  markAllNotificationsRead as apiMarkAllRead,
  markNotificationRead as apiMarkRead,
} from "../api";
import type {
  ConnectionStatus,
  NotificationDto,
  StoredNotification,
} from "../types";

/** Teto de itens mantidos em memória (a lista completa fica no servidor). */
const MAX_ITEMS = 200;

interface NotificationsState {
  notifications: StoredNotification[];
  unreadCount: number;
  connectionStatus: ConnectionStatus;
  /** Quando o status virou "disconnected" pela última vez (epoch ms). */
  disconnectedSince: number | null;

  /** Carrega/reconciencia o histórico vindo do REST (merge por id). */
  hydrate: (items: NotificationDto[], unreadCount: number) => void;
  /** Insere ou atualiza por id — usado pelo push SSE (dedup com o REST). */
  upsert: (dto: NotificationDto) => void;
  /** Define a contagem de não lidas (sinal slim / heartbeat do servidor). */
  setUnreadCount: (n: number) => void;
  /** Marca como lida: otimista local + persiste no servidor. */
  markAsRead: (id: string) => Promise<void>;
  /** Marca todas como lidas: otimista local + persiste no servidor. */
  markAllAsRead: () => Promise<void>;
  /** Remove: otimista local + persiste no servidor. */
  remove: (id: string) => Promise<void>;
  clear: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
}

function fromDto(dto: NotificationDto): StoredNotification {
  return {
    id: dto.id,
    type: dto.type,
    payload: dto.payload,
    receivedAt: dto.createdAt,
    read: dto.read,
  } as StoredNotification;
}

function sortNewestFirst(items: StoredNotification[]): StoredNotification[] {
  // receivedAt é ISO 8601 → ordenação lexicográfica = cronológica.
  return [...items].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

export const useNotificationsStore = create<NotificationsState>((set) => ({
  notifications: [],
  unreadCount: 0,
  connectionStatus: "disconnected",
  disconnectedSince: Date.now(),

  hydrate: (items, unreadCount) =>
    set((s) => {
      const byId = new Map(s.notifications.map((n) => [n.id, n]));
      for (const dto of items) byId.set(dto.id, fromDto(dto));
      return {
        notifications: sortNewestFirst([...byId.values()]).slice(0, MAX_ITEMS),
        // O servidor é a fonte de verdade da contagem total de não lidas.
        unreadCount,
      };
    }),

  upsert: (dto) =>
    set((s) => {
      const item = fromDto(dto);
      const exists = s.notifications.some((n) => n.id === item.id);
      const notifications = exists
        ? s.notifications.map((n) => (n.id === item.id ? item : n))
        : sortNewestFirst([item, ...s.notifications]).slice(0, MAX_ITEMS);
      const unreadCount = exists || item.read ? s.unreadCount : s.unreadCount + 1;
      return { notifications, unreadCount };
    }),

  setUnreadCount: (n) => set({ unreadCount: Math.max(0, n) }),

  markAsRead: async (id) => {
    let changed = false;
    set((s) => {
      const target = s.notifications.find((n) => n.id === id);
      if (!target || target.read) return s;
      changed = true;
      return {
        notifications: s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, s.unreadCount - 1),
      };
    });
    if (changed) {
      try {
        await apiMarkRead(id);
      } catch {
        // Best-effort: a próxima reconciliação (reconexão) corrige o estado.
      }
    }
  },

  markAllAsRead: async () => {
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
    try {
      await apiMarkAllRead();
    } catch {
      // Best-effort: reconciliação corrige na reconexão.
    }
  },

  remove: async (id) => {
    set((s) => {
      const target = s.notifications.find((n) => n.id === id);
      if (!target) return s;
      return {
        notifications: s.notifications.filter((n) => n.id !== id),
        unreadCount: target.read ? s.unreadCount : Math.max(0, s.unreadCount - 1),
      };
    });
    try {
      await apiDelete(id);
    } catch {
      // Best-effort: reconciliação corrige na reconexão.
    }
  },

  clear: () =>
    set({
      notifications: [],
      unreadCount: 0,
    }),

  setConnectionStatus: (status) =>
    set((s) => {
      if (s.connectionStatus === status) return s;
      return {
        connectionStatus: status,
        disconnectedSince: status === "disconnected" ? Date.now() : null,
      };
    }),
}));
